import { useEffect, useRef, type ReactNode } from 'react';
import { cx } from '../internal/cx';

export type TrailStepStatus = 'pending' | 'current' | 'done';

export interface TrailStep {
  id: string;
  label: ReactNode;
  status: TrailStepStatus;
  description?: ReactNode;
}

export interface TrailStepperProps {
  steps: TrailStep[];
  className?: string;
  'aria-label'?: string;
}

const STATUS_TEXT: Record<TrailStepStatus, string> = {
  pending: 'pendente',
  current: 'etapa atual',
  done: 'concluída',
};

/**
 * A trilha do wizard — linha horizontal com nós, avanço por etapas
 * (BRAND-EXTRACT §7). Nunca usa Proof Lime: `done` aqui é progresso de
 * navegação/formulário, não prova/conclusão/verificado no sentido do brandbook —
 * essa distinção é o que faz `TrailTimeline` (não este componente) legitimar o uso
 * de Lime. `current` é um nó azul sólido sem glifo (evita texto Paper pequeno
 * sobre Blue, que não passa AA — ver `tokens/colors.ts`); `done` é um nó com anel
 * azul e check em Ink sobre Paper (15,89:1, sempre seguro).
 *
 * Mobile-first: em telas estreitas (375px) a lista rola horizontalmente
 * (`overflow-x: auto`) mantendo a sequência — nunca reflow para layout circular
 * ou "teia".
 */
export function TrailStepper({ steps, className, ...rest }: TrailStepperProps) {
  const total = steps.length;
  const listRef = useRef<HTMLOListElement>(null);
  const currentId = steps.find((step) => step.status === 'current')?.id;

  useEffect(() => {
    const list = listRef.current;
    const node = list?.querySelector<HTMLElement>('[aria-current="step"]');
    // Só quando a lista realmente rola (mobile): num wizard de 6 passos a 375px,
    // sem isto o passo ativo fica fora da janela e a trilha mostra o começo do
    // fluxo enquanto o usuário está no fim. Rola SÓ o `scrollLeft` do próprio
    // contêiner — nunca `scrollIntoView`, que pode arrastar a rolagem VERTICAL
    // de qualquer ancestral (e da página) junto.
    if (!list || !node || list.scrollWidth <= list.clientWidth) return;
    list.scrollTo?.({ left: node.offsetLeft - (list.clientWidth - node.offsetWidth) / 2 });
  }, [currentId]);

  return (
    <ol
      ref={listRef}
      className={cx('argink-trail-stepper', className)}
      aria-label={rest['aria-label'] ?? 'Etapas'}
    >
      {steps.map((step, index) => (
        <li
          key={step.id}
          className={cx('argink-trail-stepper__step', `argink-trail-stepper__step--${step.status}`)}
          aria-current={step.status === 'current' ? 'step' : undefined}
        >
          <span className="argink-trail-stepper__node" aria-hidden="true" />
          <span className="argink-trail-stepper__body">
            <span className="argink-sr-only">
              Etapa {index + 1} de {total}:{' '}
            </span>
            <span className="argink-trail-stepper__label">{step.label}</span>
            <span className="argink-sr-only"> ({STATUS_TEXT[step.status]})</span>
            {step.description ? (
              <span className="argink-trail-stepper__description">{step.description}</span>
            ) : null}
          </span>
        </li>
      ))}
    </ol>
  );
}
